import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Check, Loader2, Copy, User, CheckCircle, Gift, Info, ArrowLeft, ShoppingBag, ShieldCheck, ReceiptText, Download, Maximize2, Sparkles, ChevronDown, RefreshCw } from 'lucide-react';
import { ref, set, get } from 'firebase/database';
import { database } from '../../services/firebase';
import { DB_PATHS } from '../../services/schema';
import { requestOrderCheckout } from '../../services/workerNotificationService';
import { getLiveRates, prefetchLiveRates, getCachedLiveRate, subscribeLiveRates } from '../../services/ratesService';
import { normalizeProductFromRTDB, resolvePriceForValidity, resolveAddonPriceForBaseValidity, calculateExtensionPrice, formatDurationLabel } from '../../utils/helpers';
import { clearCart, updatePrices, setCartOpen } from '../../store/slices/cartSlice';
import { fetchPaymentMethods, setSelectedPaymentMethod } from '../../store/slices/paymentSlice';
import Button from '../common/Button';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { validateRewardPointsUsage, calculateRewardPointsDiscount, calculateCouponDiscount, getIndiaDateTime, formatPrice } from '../../utils/helpers';
import { calculateDiscountedPrice } from '../../utils/priceUtils';
import { getAuthRedirectPath } from '../../utils/authRedirect';
import { shouldApplyExtensionDiscount } from '../../utils/extensionEligibility';
import PaymentMethodIcon from '../common/PaymentMethodIcon';

const PRODUCT_CATEGORIES = ['premium', 'exclusive', 'bundle', 'addon', 'other', 'free'];

export const isLitecoinMethod = (method) => {
  if (!method) return false;
  const icon = String(method.iconKey || method.icon || '').toLowerCase();
  const symbol = String(method.symbol || '').toUpperCase();
  const name = String(method.name || '').toLowerCase();
  return icon === 'ltc' || symbol === 'LTC' || name.includes('litecoin') || name === 'ltc';
};

export const isAmazonMethod = (method) => {
  if (!method) return false;
  const icon = String(method.iconKey || method.icon || '').toLowerCase();
  const name = String(method.name || '').toLowerCase();
  const category = String(method.category || '').toLowerCase();
  return icon === 'amazon' || name.includes('amazon') || category === 'gift card';
};

const CheckoutModal = ({ isOpen, onClose, directItems = null, directMode = null, onDirectOrderCreated = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items: storeCartItems, appliedPoints: storeAppliedPoints, appliedCoupon: storeAppliedCoupon } = useSelector((state) => state.cart);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { paymentMethods, selectedPaymentMethod, loading: paymentLoading } = useSelector((state) => state.payment);
  const { settings } = useSelector((state) => state.settings);
  const storeDiscounts = useSelector((state) => state.products.storeDiscounts);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    transactionId: '',
    giftCardNumber: '',
  });

  const [loading, setLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const initialCachedRate = getCachedLiveRate();
  const [exchangeRates, setExchangeRates] = useState({
    ltc: initialCachedRate?.ltc || null,
    ltcUsdtPrice: initialCachedRate?.ltcUsdtPrice || null,
    inr: initialCachedRate?.inr || null,
    source: initialCachedRate?.source || null,
    loading: false,
    lastUpdated: initialCachedRate?.lastUpdated || null,
    fetchedAt: initialCachedRate?.fetchedAt || null,
    error: null
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [expandedQrOpen, setExpandedQrOpen] = useState(false);
  const [mobileSummaryExpanded, setMobileSummaryExpanded] = useState(false);
  const lastValidationKeyRef = useRef('');
  const contentScrollRef = useRef(null);
  const modalShellRef = useRef(null);
  const transactionIdInputRef = useRef(null);
  const giftCardInputRef = useRef(null);
  const suppressNextSubmitRef = useRef(false);
  const checkoutIdempotencyKeyRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      checkoutIdempotencyKeyRef.current = null;
    } else {
      prefetchLiveRates();
    }
  }, [isOpen]);

  useEffect(() => {
    const unsubscribe = subscribeLiveRates((data) => {
      if (data && data.ltc && data.ltcUsdtPrice > 0) {
        setExchangeRates(prev => ({
          ...prev,
          ...data,
          loading: false,
          error: null
        }));
      }
    });
    return unsubscribe;
  }, []);
  const cartItems = React.useMemo(
    () => (Array.isArray(directItems) && directItems.length > 0 ? directItems : storeCartItems),
    [directItems, storeCartItems]
  );
  const isDirectCheckout = Array.isArray(directItems) && directItems.length > 0;
  const hasScriptRequestItem = cartItems.some((item) => item.itemType === 'script-request' || item.category === 'script-request');
  const isScriptRequestCheckout = directMode === 'script-request' && isDirectCheckout && hasScriptRequestItem;
  const hasPointsTopUpItem = cartItems.some((item) => item.itemType === 'points-topup' || item.category === 'points-topup');
  const isPointsTopUpCheckout = directMode === 'points-topup' && isDirectCheckout && hasPointsTopUpItem;
  // Direct checkout already forces both to zero; top-ups must never be paid with points or coupons.
  const appliedPoints = isDirectCheckout ? 0 : storeAppliedPoints;
  const appliedCoupon = isDirectCheckout ? null : storeAppliedCoupon;

  const fetchLiveRates = useCallback(async ({ force = false, silent = true } = {}) => {
    const cached = getCachedLiveRate();
    if (!force && cached && cached.ltc && cached.ltcUsdtPrice > 0) {
      setExchangeRates(prev => ({
        ...prev,
        ...cached,
        loading: false,
        error: null
      }));
      return cached;
    }

    if (!silent) {
      setExchangeRates(prev => ({ ...prev, loading: true, error: null }));
    }

    try {
      const rateData = await getLiveRates({ force });
      if (rateData && rateData.ltc && rateData.ltcUsdtPrice > 0) {
        setExchangeRates(prev => ({
          ...prev,
          ...rateData,
          loading: false,
          error: null
        }));
        return rateData;
      }
      throw new Error("Unable to retrieve live rates");
    } catch (error) {
      console.warn("Error fetching live rates:", error);
      setExchangeRates(prev => ({
        ...prev,
        loading: false,
        error: "Failed to fetch live rates"
      }));
      if (!silent) {
        toast.error("Failed to fetch live exchange rates");
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (isLitecoinMethod(selectedPaymentMethod)) {
      const cached = getCachedLiveRate();
      if (!cached || !cached.ltc) {
        fetchLiveRates({ force: false, silent: true });
      }
    }
  }, [selectedPaymentMethod, fetchLiveRates]);

  useEffect(() => {
    if (step === 2 && isLitecoinMethod(selectedPaymentMethod)) {
      const cached = getCachedLiveRate();
      if (!cached || !cached.ltc) {
        fetchLiveRates({ force: false, silent: true });
      }
    }
  }, [step, selectedPaymentMethod, fetchLiveRates]);

  useEffect(() => {
    if (!isOpen || paymentMethods.length === 0) return;
    const activeMethods = paymentMethods.filter((method) => String(method.status || 'active').toLowerCase() === 'active');
    if (activeMethods.length === 0) {
      if (selectedPaymentMethod) dispatch(setSelectedPaymentMethod(null));
      return;
    }
    const selectedIsActive = activeMethods.some((method) => method.id === selectedPaymentMethod?.id);
    if (!selectedIsActive) {
      dispatch(setSelectedPaymentMethod(activeMethods[0]));
    }
  }, [dispatch, isOpen, paymentMethods, selectedPaymentMethod]);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setFormData({ transactionId: '', giftCardNumber: '' });
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.requestAnimationFrame(() => {
      modalShellRef.current?.focus({ preventScroll: true });
    });
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.requestAnimationFrame(() => {
      modalShellRef.current?.focus({ preventScroll: true });
    });
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isOpen, step]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '-' + window.scrollY + 'px';
    } else {
      document.body.classList.remove('modal-open');
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo({
        top: parseInt(scrollY || '0') * -1,
        behavior: 'instant'
      });
    }

    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePaymentMethodSelect = (method) => {
    dispatch(setSelectedPaymentMethod(method));
    if (isLitecoinMethod(method)) {
      const cached = getCachedLiveRate();
      const isFresh = (cached && cached.ltc > 0) || (exchangeRates.ltc && exchangeRates.ltcUsdtPrice > 0 && exchangeRates.fetchedAt && (Date.now() - exchangeRates.fetchedAt < 60000));
      if (!isFresh) {
        fetchLiveRates({ force: false, silent: true });
      }
    }
  };

  const focusProofInput = useCallback((fieldName) => {
    const input = fieldName === 'giftCardNumber'
      ? giftCardInputRef.current
      : transactionIdInputRef.current;
    if (!input) return;

    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.requestAnimationFrame(() => {
      input.focus({ preventScroll: true });
    });
  }, []);

  const resolvePriceValue = useCallback((price) => {
    if (typeof price === 'number' && Number.isFinite(price)) return price;
    const parsed = parseFloat(String(price ?? '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const normalizeProductStatus = useCallback((product) => String(product?.status || product?.Status || 'active').toLowerCase(), []);
  const getCatalogProductId = useCallback((item) => {
    if (item?.itemType === 'script-request' || item?.category === 'script-request') return null;
    if (item?.itemType === 'points-topup' || item?.category === 'points-topup') return null;
    if (item?.addonFor) return item.addonFor;
    return item?.extensionFor ? String(item.id || '').replace(/-extension$/, '') : item?.id;
  }, []);
  const normalizeProductCategory = useCallback((category) => {
    const normalized = String(category || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
    if (normalized === 'others') return 'other';
    if (normalized === 'free script' || normalized === 'free scripts') return 'free';
    return PRODUCT_CATEGORIES.includes(normalized) ? normalized : '';
  }, []);

  const fetchCartProducts = useCallback(async () => {
    const requested = new Map();
    cartItems.forEach((item) => {
      const productId = getCatalogProductId(item);
      if (!productId) return;
      const preferredCategory = normalizeProductCategory(item.category);
      requested.set(String(productId), preferredCategory);
    });

    const entries = await Promise.all(Array.from(requested.entries()).map(async ([productId, preferredCategory]) => {
      const categories = preferredCategory
        ? [preferredCategory, ...PRODUCT_CATEGORIES.filter(category => category !== preferredCategory)]
        : PRODUCT_CATEGORIES;

      for (const category of categories) {
        const snapshot = await get(ref(database, `${DB_PATHS.PRODUCTS}/${category}/${productId}`));
        if (snapshot.exists()) {
          return normalizeProductFromRTDB(category, productId, snapshot.val());
        }
      }
      return null;
    }));

    return entries.filter(Boolean);
  }, [cartItems, getCatalogProductId, normalizeProductCategory]);

  const getCurrentPrice = useCallback((productId, validity, productsLookup, discounts = storeDiscounts) => {
    const product = productsLookup instanceof Map
      ? productsLookup.get(productId)
      : productsLookup.find(p => p.id === productId);
    if (!product) return null;

    let basePrice = product.price;

    const matchedPrice = resolvePriceForValidity(product.pricingRaw || product.Price || {}, validity);
    if (matchedPrice !== null) {
      basePrice = matchedPrice;
    } else if (typeof product.price === 'number') {
      basePrice = product.price;
    } else {
      const priceMap = product.pricingRaw || product.Price || {};
      const nums = Object.values(priceMap).filter(v => typeof v === 'number');
      basePrice = nums.length ? Math.min(...nums) : null;
    }

    if (basePrice === null) return null;

    const { finalPrice } = calculateDiscountedPrice(product, basePrice, validity, discounts);
    return finalPrice;
  }, [storeDiscounts]);

  const findAddonForCartItem = useCallback((product, item) => {
    if (!product || !item?.addonId) return null;
    const addons = Array.isArray(product.addons) ? product.addons : [];
    return addons.find((addon) => String(addon.id) === String(item.addonId)) || null;
  }, []);

  const getCurrentExtensionItemPrice = useCallback((item, product) => {
    if (!item?.extensionFor || !product) return null;
    const priceMap = product.pricingRaw || product.Price || {};

    if (item.upgradeToValidity) {
      const toPrice = resolvePriceForValidity(priceMap, item.upgradeToValidity);
      const fromPrice = resolvePriceForValidity(priceMap, item.upgradeFromValidity);
      if (toPrice === null || fromPrice === null || Number(toPrice) <= Number(fromPrice)) return null;
      return Number((Number(toPrice) - Number(fromPrice)).toFixed(2));
    }

    const basePrice = resolvePriceForValidity(priceMap, item.validity);
    if (basePrice === null) return null;

    return shouldApplyExtensionDiscount(item)
      ? calculateExtensionPrice(basePrice, item.validity, settings?.extensionDiscount || {})
      : Number(basePrice);
  }, [settings?.extensionDiscount]);

  const getCurrentCartItemPrice = useCallback((item, productsLookup, discounts = storeDiscounts) => {
    const productId = getCatalogProductId(item);
    const product = productsLookup instanceof Map
      ? productsLookup.get(productId)
      : productsLookup.find(p => p.id === productId);
    if (!product) return null;

    if (item.extensionFor) {
      return getCurrentExtensionItemPrice(item, product);
    }

    if (item.addonFor) {
      const addon = findAddonForCartItem(product, item);
      if (!addon) return null;
      const adjustedPrice = resolveAddonPriceForBaseValidity(
        addon.pricingRaw || addon.Price || {},
        product.pricingRaw || product.Price || {},
        item.validity
      );
      const basePrice = adjustedPrice.price;
      if (basePrice === null) return null;
      const addonProduct = {
        ...addon,
        id: item.id,
        category: 'addon',
        addonFor: item.addonFor,
        addonId: item.addonId,
        baseProductId: product.id,
        baseProductName: product.name
      };
      const { finalPrice } = calculateDiscountedPrice(addonProduct, basePrice, item.validity, discounts);
      return finalPrice;
    }

    return getCurrentPrice(productId, item.validity, productsLookup, discounts);
  }, [findAddonForCartItem, getCatalogProductId, getCurrentExtensionItemPrice, getCurrentPrice, storeDiscounts]);

  const buildOrderItems = useCallback(() => cartItems.map((item) => {
    const isScriptRequestItem = item.itemType === 'script-request' || item.category === 'script-request';
    const isPointsTopUpItem = item.itemType === 'points-topup' || item.category === 'points-topup';
    const isAddonItem = !isPointsTopUpItem && Boolean(item.addonFor || item.addonId);
    const isExtensionItem = Boolean(item.extensionFor);
    const productId = isPointsTopUpItem
      ? null
      : isAddonItem
        ? item.addonFor
        : isExtensionItem
          ? String(item.id || '').replace(/-extension$/, '')
          : item.id;

    return {
      productId,
      productName: String(item.name || item.productName || item.id || '').trim(),
      quantity: item.quantity || 1,
      validity: item.validity || null,
      price: resolvePriceValue(item.price),
      itemType: isScriptRequestItem
        ? 'script-request'
        : isPointsTopUpItem
          ? 'points-topup'
          : isAddonItem
            ? 'addon'
            : isExtensionItem
              ? 'extension'
              : 'product',
      ...(isPointsTopUpItem && {
        pointsAmount: Number(item.pointsAmount) || 0,
        basePoints: Number(item.basePoints) || 0,
        bonusPoints: Number(item.bonusPoints) || 0,
        bonusPercent: Number(item.bonusPercent) || 0,
        pointsRate: Number(item.pointsRate) || 20
      }),
      ...(isScriptRequestItem && {
        requestId: item.requestId || null,
        gameName: item.gameName || item.name || '',
        storeLink: item.storeLink || '',
        note: item.note || '',
        requestFee: item.requestFee || item.price || 0
      }),
      ...(isAddonItem && {
        cartItemId: item.id,
        addonFor: item.addonFor,
        addonId: item.addonId,
        addonName: item.addonName || item.name,
        baseProductName: item.baseProductName || item.addonFor,
        addonPricingSource: item.addonPricingSource || null,
        addonAdjustedDays: item.addonAdjustedDays || null,
        addonBaseValidity: item.addonBaseValidity || null
      }),
      ...(isExtensionItem && {
        extensionFor: item.extensionFor,
        renewal: Boolean(item.renewal),
        extensionDiscountEligible: Boolean(item.extensionDiscountEligible),
        accessSource: item.accessSource || null,
        remainingDays: item.remainingDays ?? null
      }),
      ...(item.upgradeFromValidity && { upgradeFromValidity: item.upgradeFromValidity }),
      ...(item.upgradeToValidity && { upgradeToValidity: item.upgradeToValidity }),
      ...(item.upgradeDays && { upgradeDays: item.upgradeDays })
    };
  }), [cartItems, resolvePriceValue]);

  const calculateOrderTotals = useCallback(() => {
    const subtotalRaw = cartItems.reduce((total, item) => {
      return total + resolvePriceValue(item.price) * (item.quantity || 1);
    }, 0);
    const subtotal = Number(subtotalRaw.toFixed(2));
    const shipping = 0;
    const pointsRate = settings?.pointsRate || 20;
    const pointsDiscount = calculateRewardPointsDiscount(appliedPoints, pointsRate);
    const promoDiscount = appliedCoupon ? calculateCouponDiscount(appliedCoupon, cartItems) : 0;
    const commissionRate = settings?.commission || 15;
    const commissionFee = (selectedPaymentMethod?.applyCommission)
      ? Number((subtotal * commissionRate / 100).toFixed(2))
      : 0;

    const total = Math.max(0, Number((subtotal + shipping + commissionFee - pointsDiscount - promoDiscount).toFixed(2)));
    return { subtotal, shipping, pointsDiscount, promoDiscount, commissionFee, total };
  }, [
    appliedCoupon,
    appliedPoints,
    cartItems,
    resolvePriceValue,
    selectedPaymentMethod?.applyCommission,
    settings?.commission,
    settings?.pointsRate
  ]);

  const placeFreeOrder = useCallback(async () => {
    setLoading(true);
    try {
      if (!checkoutIdempotencyKeyRef.current) {
        checkoutIdempotencyKeyRef.current =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : 'ord_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 10);
      }
      const orderItems = buildOrderItems();
      const checkoutPayload = {
        items: orderItems,
        paymentMethod: {
          name: appliedCoupon?.code
            ? (appliedPoints > 0 ? `Coupon (${appliedCoupon.code}) & Points (100% Covered)` : `Coupon 100% Discount (${appliedCoupon.code})`)
            : 'Reward Points (100% Covered)',
          category: 'Free',
          transactionId: 'AUTO-ZERO-PAID'
        },
        couponCode: appliedCoupon?.code || null,
        pointsToUse: appliedPoints || 0,
        notes: '100% covered order',
        idempotencyKey: checkoutIdempotencyKeyRef.current
      };

      const result = await requestOrderCheckout(checkoutPayload);
      checkoutIdempotencyKeyRef.current = null;

      if (!isDirectCheckout) {
        dispatch(clearCart());
      }
      if (isDirectCheckout && typeof onDirectOrderCreated === 'function') {
        await onDirectOrderCreated({ orderId: result.orderId, order: result, orderItems });
      }
      toast.success(result?.isReplay ? `Order #${result.orderId} verified!` : `Free order #${result.orderId} placed successfully!`);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Free order creation failed:', error);
      if (error?.status === 409) {
        toast.error('Your order is already processing. Please wait a moment.');
      } else {
        toast.error(error?.message || 'Failed to place free order. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [
    appliedCoupon,
    appliedPoints,
    buildOrderItems,
    dispatch,
    isDirectCheckout,
    onDirectOrderCreated
  ]);

  const validateCartProducts = useCallback(async () => {
    if (cartItems.length === 0) return;
    if (isScriptRequestCheckout) return;
    // Top-ups have no catalog product to validate against, and must never route to the free-order path.
    if (isPointsTopUpCheckout) return;

    const productsList = await fetchCartProducts();
    const productsById = new Map((productsList || []).map(product => [product.id, product]));
    const unavailableItems = [];
    let hasChanges = false;
    const updatedItems = cartItems.map(item => {
      const productId = getCatalogProductId(item);
      const product = productsById.get(productId);

      if (!product || !['active', 'free'].includes(normalizeProductStatus(product))) {
        unavailableItems.push(item);
        return item;
      }

      if (item.addonFor) {
        const addon = findAddonForCartItem(product, item);
        const addonStatus = String(addon?.status || 'active').toLowerCase();
        if (!addon || addonStatus !== 'active') {
          unavailableItems.push(item);
          return item;
        }
      }

      const currentPrice = getCurrentCartItemPrice(item, productsById, storeDiscounts);
      if (currentPrice !== null && Math.abs(currentPrice - resolvePriceValue(item.price)) > 0.01) {
        hasChanges = true;
        return { ...item, price: currentPrice, priceUpdated: true };
      }
      return item;
    });

    if (unavailableItems.length > 0) {
      toast.error(`${unavailableItems[0].name || 'A cart item'} is no longer available. Please remove it to continue.`);
      onClose();
      dispatch(setCartOpen(true));
    } else if (hasChanges) {
      dispatch(updatePrices(updatedItems));
      toast.error('Product prices have changed. Please review your cart.');
      onClose();
      dispatch(setCartOpen(true));
    } else {
      const currentTotals = calculateOrderTotals();
      if (currentTotals.total === 0) {
        await placeFreeOrder();
      }
    }
  }, [
    calculateOrderTotals,
    cartItems,
    dispatch,
    fetchCartProducts,
    findAddonForCartItem,
    getCatalogProductId,
    getCurrentCartItemPrice,
    normalizeProductStatus,
    onClose,
    placeFreeOrder,
    resolvePriceValue,
    isScriptRequestCheckout,
    isPointsTopUpCheckout,
    storeDiscounts
  ]);

  useEffect(() => {
    if (!isOpen) {
      lastValidationKeyRef.current = '';
      return;
    }

    const validationKey = cartItems
      .map((item) => [
        item.id,
        item.validity,
        item.price,
        item.quantity,
        item.extensionFor || '',
        item.renewal ? 'renewal' : '',
        item.extensionDiscountEligible ? 'extension-discount' : 'regular-extension',
        item.remainingDays ?? '',
        item.upgradeFromValidity || '',
        item.upgradeToValidity || '',
        item.upgradeDays || '',
        item.addonFor || '',
        item.addonId || '',
        item.addonAdjustedDays || '',
        item.addonPricingSource || '',
        item.requestId || '',
        item.itemType || ''
      ].join(':'))
      .join('|');

    if (lastValidationKeyRef.current === validationKey) return;
    lastValidationKeyRef.current = validationKey;

    dispatch(fetchPaymentMethods());
    validateCartProducts().catch((err) => {
      lastValidationKeyRef.current = '';
      console.error("Failed to validate cart products", err);
      toast.error('Unable to validate your cart. Please try again.');
    });
  }, [isOpen, dispatch, validateCartProducts, cartItems]);

  const orderTotals = calculateOrderTotals();
  const { subtotal, shipping, pointsDiscount, promoDiscount, commissionFee, total: finalTotal } = orderTotals;
  const isZeroTotal = finalTotal <= 0;

  useEffect(() => {
    if (isZeroTotal && step === 2) {
      setStep(1);
    }
  }, [isZeroTotal, step]);

  const calculatePaymentAmount = (orderTotal) => {
    const baseTotal = Number(orderTotal ?? 0);
    if (!selectedPaymentMethod) {
      return { amount: baseTotal.toFixed(2), currency: 'USDT' };
    }

    if (isLitecoinMethod(selectedPaymentMethod)) {
      if (exchangeRates.ltc && exchangeRates.ltcUsdtPrice > 0) {
        const ltcAmountRaw = baseTotal * exchangeRates.ltc;
        const ltcAmount = Math.max(0, Math.round(ltcAmountRaw * 1000) / 1000);
        return { amount: ltcAmount.toFixed(3), currency: 'LTC' };
      }
      return { amount: exchangeRates.loading ? 'Fetching live...' : 'Rate pending', currency: 'LTC' };
    }

    if (isAmazonMethod(selectedPaymentMethod)) {
      const inrRate = Number(settings?.exchangeRateINR) || 92;
      return { amount: Math.round(baseTotal * inrRate).toString(), currency: 'INR' };
    }

    const icon = String(selectedPaymentMethod.iconKey || selectedPaymentMethod.icon || '').toLowerCase();
    const sym = String(selectedPaymentMethod.symbol || '').toUpperCase();
    if (icon.includes('usdc') || sym === 'USDC') {
      return { amount: baseTotal.toFixed(2), currency: 'USDC' };
    }

    if (icon.includes('usdt') || sym === 'USDT' || selectedPaymentMethod.name === 'Binance Pay') {
      return { amount: baseTotal.toFixed(2), currency: 'USDT' };
    }

    return { amount: baseTotal.toFixed(2), currency: 'USD' };
  };

  const paymentSummary = calculatePaymentAmount(finalTotal);
  const activePaymentMethods = paymentMethods.filter(method => String(method.status || 'active').toLowerCase() === 'active');
  const currentStepSubtitle = step === 1
    ? isScriptRequestCheckout ? 'Select payment method for your script request.' : 'Select how you want to pay for this order.'
    : isScriptRequestCheckout ? 'Pay the request fee, then paste your payment proof.' : 'Send the exact amount, then paste your payment proof.';
  const proofLabel = selectedPaymentMethod?.category === 'Gift Card'
    ? 'Gift card number or claim code'
    : 'Transaction reference or hash';
  const proofPlaceholder = selectedPaymentMethod?.category === 'Gift Card'
    ? 'XXXX-XXXXXX-XXXX'
    : 'Paste transaction hash or reference here';

  const proceedToPaymentDetails = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return false;
    }

    if (isLitecoinMethod(selectedPaymentMethod)) {
      // Fast check: return immediately if cached or current rate is fresh (< 60s)
      const cached = getCachedLiveRate();
      const current = (cached && cached.ltc > 0)
        ? cached
        : (exchangeRates.ltc && exchangeRates.ltcUsdtPrice > 0 && exchangeRates.fetchedAt && (Date.now() - exchangeRates.fetchedAt < 60000) ? exchangeRates : null);

      if (!current || !current.ltc || current.ltc <= 0) {
        setExchangeRates(prev => ({ ...prev, loading: true, error: null }));
        const freshRate = await getLiveRates({ force: false });
        setExchangeRates(prev => ({ ...prev, loading: false }));

        if (!freshRate || !freshRate.ltc || freshRate.ltc <= 0 || !freshRate.ltcUsdtPrice) {
          toast.error('Cannot proceed with Litecoin: Unable to retrieve live market rate. Please try again or select another payment method.');
          return false; // STRICT GATE: DO NOT OPEN STEP 2!
        }
      }
    }

    setStep(2);
    return true;
  };

  const handleNextStep = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    suppressNextSubmitRef.current = true;
    window.setTimeout(() => {
      suppressNextSubmitRef.current = false;
    }, 500);

    const currentTotals = calculateOrderTotals();
    if (currentTotals.total <= 0) {
      placeFreeOrder();
      return;
    }

    if (step < 2) {
      await proceedToPaymentDetails();
    }
  };

  const handlePrevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (suppressNextSubmitRef.current) {
      suppressNextSubmitRef.current = false;
      return;
    }

    const currentTotals = calculateOrderTotals();
    if (currentTotals.total <= 0) {
      await placeFreeOrder();
      return;
    }

    if (step < 2) {
      await proceedToPaymentDetails();
      return;
    }

    setLoading(true);

    try {
      if (currentTotals.total > 0 && !selectedPaymentMethod) {
        toast.error('Please select a payment method');
        setLoading(false);
        return;
      }

      if (appliedPoints > 0) {
        const userPoints = user?.rewardPoints || 0;
        const pointsRate = settings?.pointsRate || 20;
        const validation = validateRewardPointsUsage(appliedPoints, userPoints, currentTotals.subtotal, pointsRate);

        if (!validation.valid) {
          toast.error(`Reward Points Error: ${validation.error}`);
          setLoading(false);
          return;
        }

        if (appliedPoints > userPoints) {
          toast.error(`Insufficient reward points. You have ${userPoints} points but trying to use ${appliedPoints}.`);
          setLoading(false);
          return;
        }
      }

      if (currentTotals.total > 0) {
        if (selectedPaymentMethod?.category === 'Crypto' && !formData.transactionId.trim()) {
          toast.error('Transaction ID is required for crypto payments');
          focusProofInput('transactionId');
          setLoading(false);
          return;
        }
        if (selectedPaymentMethod?.category === 'Gift Card' && !formData.giftCardNumber.trim()) {
          toast.error('Gift card number is required');
          focusProofInput('giftCardNumber');
          setLoading(false);
          return;
        }
      }

      // Verify live rate before transaction processing
      let liveLtcData = null;
      if (isLitecoinMethod(selectedPaymentMethod)) {
        const cached = getCachedLiveRate();
        liveLtcData = (cached && cached.ltc > 0) ? cached : await getLiveRates({ force: false });
        if (!liveLtcData?.ltc && !exchangeRates.ltc) {
          toast.error("Unable to verify live LTC exchange rate before payment. Please refresh rates and try again.");
          setLoading(false);
          return;
        }
      }

      const activeLtcRate = liveLtcData?.ltc || exchangeRates.ltc;
      const finalLtcAmount = activeLtcRate
        ? Math.max(0, Math.round(currentTotals.total * activeLtcRate * 1000) / 1000).toFixed(3)
        : null;

      const orderItems = buildOrderItems();
      const isScriptRequestOrder = isScriptRequestCheckout;
      const formattedDate = await getIndiaDateTime();
      const paymentDetails = {
        Amount: currentTotals.total,
        'Pay method': selectedPaymentMethod.name,
        'Transaction date': formattedDate
      };

      if (isLitecoinMethod(selectedPaymentMethod)) {
        paymentDetails["Currency"] = "LTC";
        if (finalLtcAmount) {
          paymentDetails["Converted amount"] = finalLtcAmount;
        }
        if (activeLtcRate) {
          paymentDetails["Exchange rate"] = activeLtcRate;
        }
        paymentDetails["LTC Price USD"] = liveLtcData?.ltcUsdtPrice || exchangeRates.ltcUsdtPrice || null;
        paymentDetails["Rate source"] = liveLtcData?.source || exchangeRates.source || "live";
        paymentDetails["Rate fetched at"] = new Date().toISOString();
      } else if (isAmazonMethod(selectedPaymentMethod)) {
        const inrRate = Number(settings?.exchangeRateINR) || 92;
        paymentDetails["Currency"] = "INR";
        paymentDetails["Converted amount"] = Math.round(currentTotals.total * inrRate).toString();
        paymentDetails["Exchange rate"] = inrRate;
      }

      if (selectedPaymentMethod?.category === 'Crypto' && formData.transactionId.trim()) {
        paymentDetails['Transaction id'] = formData.transactionId.trim();
      } else if (selectedPaymentMethod?.category === 'Gift Card' && formData.giftCardNumber.trim()) {
        paymentDetails['Gift card number'] = formData.giftCardNumber.trim();
      } else if (formData.transactionId.trim()) {
        paymentDetails['Transaction id'] = formData.transactionId.trim();
      } else {
        paymentDetails['Transaction id'] = 'N/A';
      }

      if (isScriptRequestOrder) {
        const requestItem = orderItems.find((item) => item.itemType === 'script-request');
        if (!requestItem?.requestId) {
          toast.error('Unable to submit request. Please reopen the request form.');
          setLoading(false);
          return;
        }

        const proofValue = selectedPaymentMethod?.category === 'Gift Card'
          ? formData.giftCardNumber.trim()
          : formData.transactionId.trim();
        const proofType = selectedPaymentMethod?.category === 'Gift Card' ? 'giftCardNumber' : 'transactionId';
        const requestRecord = {
          gameName: requestItem.gameName || '',
          storeLink: requestItem.storeLink || '',
          note: requestItem.note || '',
          requestFee: resolvePriceValue(requestItem.requestFee || requestItem.price),
          userId: user.uid,
          userEmail: user?.email || 'anonymous',
          userName: user?.displayName || user?.name || 'Anonymous',
          status: 'pending',
          paymentStatus: 'submitted',
          paymentMethod: selectedPaymentMethod.name,
          paymentCategory: selectedPaymentMethod.category || '',
          paymentProofType: proofType,
          paymentProof: proofValue,
          currency: paymentDetails["Currency"] || "USD",
          ...(paymentDetails["Converted amount"] && { convertedAmount: paymentDetails["Converted amount"] }),
          ...(paymentDetails["Exchange rate"] && { exchangeRate: paymentDetails["Exchange rate"] }),
          Payments: paymentDetails,
          createdAt: formattedDate,
          updatedAt: formattedDate
        };

        await set(ref(database, `${DB_PATHS.SCRIPT_REQUESTS}/${requestItem.requestId}`), requestRecord);
        if (typeof onDirectOrderCreated === 'function') {
          await onDirectOrderCreated({ requestId: requestItem.requestId, request: requestRecord, orderItems });
        }
        toast.success('Script request submitted for payment verification.');
        setShowSuccessModal(true);
        setStep(1);
        setFormData({ transactionId: '', giftCardNumber: '' });
        return;
      }

      if (!checkoutIdempotencyKeyRef.current) {
        checkoutIdempotencyKeyRef.current =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : 'ord_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 10);
      }

      const checkoutPayload = {
        items: orderItems,
        paymentMethod: {
          name: selectedPaymentMethod.name,
          category: selectedPaymentMethod.category || '',
          transactionId: formData.transactionId?.trim() || '',
          giftCardNumber: formData.giftCardNumber?.trim() || '',
          currency: isLitecoinMethod(selectedPaymentMethod) ? "LTC" : (isAmazonMethod(selectedPaymentMethod) ? "INR" : "USD"),
          ...(isLitecoinMethod(selectedPaymentMethod) && {
            convertedAmount: finalLtcAmount,
            exchangeRate: activeLtcRate,
            rateSource: liveLtcData?.source || exchangeRates.source || "live",
            rateFetchedAt: new Date().toISOString()
          }),
          ...(isAmazonMethod(selectedPaymentMethod) && {
            convertedAmount: Math.round(currentTotals.total * (Number(settings?.exchangeRateINR) || 92)).toString(),
            exchangeRate: Number(settings?.exchangeRateINR) || 92
          })
        },
        couponCode: appliedCoupon?.code || null,
        pointsToUse: appliedPoints || 0,
        notes: '',
        idempotencyKey: checkoutIdempotencyKeyRef.current
      };

      const result = await requestOrderCheckout(checkoutPayload);
      checkoutIdempotencyKeyRef.current = null;

      if (!isDirectCheckout) {
        dispatch(clearCart());
      }
      if (isDirectCheckout && typeof onDirectOrderCreated === 'function') {
        await onDirectOrderCreated({ orderId: result.orderId, order: result, orderItems });
      }
      toast.success(result?.isReplay ? `Order #${result.orderId} verified!` : `Order #${result.orderId} placed successfully!`);
      setShowSuccessModal(true);
      setStep(1);
      setFormData({ transactionId: '', giftCardNumber: '' });
    } catch (error) {
      console.error('Order creation failed:', error);
      if (error?.status === 409) {
        toast.error('An order is already being processed. Please wait a moment.');
      } else {
        toast.error(error?.message || 'Failed to place order. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    setCopyLoading(true);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
        return;
      }
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy. Please copy manually.');
    } finally {
      setCopyLoading(false);
    }
  };

  const downloadQrCode = async () => {
    const qrCode = selectedPaymentMethod?.qrCode;
    if (!qrCode) return;

    const safeName = String(selectedPaymentMethod?.name || 'payment')
      .trim()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'payment';
    const filename = `${safeName}-qr-code.png`;

    try {
      const link = document.createElement('a');
      link.download = filename;

      if (qrCode.startsWith('data:')) {
        link.href = qrCode;
      } else {
        const response = await fetch(qrCode, { mode: 'cors' });
        if (!response.ok) throw new Error('Unable to fetch QR code');
        const blob = await response.blob();
        link.href = URL.createObjectURL(blob);
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (!qrCode.startsWith('data:')) {
        URL.revokeObjectURL(link.href);
      }

      toast.success('QR code download started');
    } catch (error) {
      window.open(qrCode, '_blank', 'noopener,noreferrer');
      toast('Opened QR code in a new tab. Long press or save it from there.');
    }
  };

  

  const renderPaymentDetailsContent = (paymentAmount, storeSavings) => (
    <div className="w-full min-w-0 max-w-full space-y-3 sm:space-y-4">
      <div className="min-w-0">
        <h3 className="text-base sm:text-lg font-black text-gray-950 dark:text-white">Payment details</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Pay the exact amount and submit the reference so the team can approve your order.</p>
      </div>

      {selectedPaymentMethod && (
        <div className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-primary-200/90 bg-gradient-to-br from-primary-50/70 via-white to-sky-50/60 shadow-sm dark:border-primary-900/50 dark:from-primary-950/40 dark:via-gray-900 dark:to-slate-900 p-3 sm:p-4">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <PaymentMethodIcon method={selectedPaymentMethod} size="md" showNetworkBadge={true} />
              <div className="min-w-0">
                <p className="truncate text-sm sm:text-base font-black leading-tight text-gray-950 dark:text-white">
                  {selectedPaymentMethod.name}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400">
                    {selectedPaymentMethod.category}
                  </span>
                  {selectedPaymentMethod.network && (
                    <span className="inline-flex rounded px-1.5 py-0.2 text-[9px] font-bold uppercase bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {selectedPaymentMethod.network}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="shrink-0 text-right">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                Amount to send
              </span>
              <div className="flex items-center justify-end gap-1.5 notranslate" translate="no">
                <span className="text-2xl sm:text-3xl font-black text-primary-600 dark:text-primary-300">
                  {paymentAmount.amount}
                </span>
                <span className="text-xs font-bold uppercase text-gray-500 dark:text-gray-400">
                  {paymentAmount.currency}
                </span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(paymentAmount.amount.toString())}
                  disabled={copyLoading}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-primary-200/80 bg-white/90 text-primary-600 shadow-xs transition hover:bg-white hover:text-primary-700 dark:border-primary-800 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-gray-750"
                  title="Copy amount to send"
                  aria-label="Copy amount to send"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>

          {(storeSavings > 0 || (selectedPaymentMethod.name === 'Litecoin' && exchangeRates.ltc) || selectedPaymentMethod.category === 'Gift Card') && (
            <div className="mt-2.5 pt-2.5 border-t border-primary-100/80 dark:border-primary-900/40 flex flex-wrap items-center justify-between gap-2 text-[11px]">
              {storeSavings > 0 && (
                <span className="inline-flex items-center gap-1 font-bold text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3 w-3" />
                  Saved {formatPrice(storeSavings)} with store discounts
                </span>
              )}
              {selectedPaymentMethod.name === 'Litecoin' && (
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">Live Rate:</span>
                    1 USDT ≈ {exchangeRates.ltc ? exchangeRates.ltc.toFixed(6) : '...'} LTC
                    {exchangeRates.ltcUsdtPrice && (
                      <span className="text-[10px] text-gray-400">(${exchangeRates.ltcUsdtPrice.toFixed(2)}/LTC)</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => fetchLiveRates({ force: true })}
                    disabled={exchangeRates.loading}
                    className="p-0.5 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    title="Refresh live rate"
                  >
                    <RefreshCw className={`h-3 w-3 ${exchangeRates.loading ? "animate-spin" : ""}`} />
                  </button>
                </div>
              )}
              {selectedPaymentMethod.category === 'Gift Card' && (
                <span className="text-gray-500 dark:text-gray-400">
                  Approx. cost: {finalTotal.toFixed(2)} USDT
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {selectedPaymentMethod && (
        <div className="w-full min-w-0 max-w-full space-y-3">
          {selectedPaymentMethod.instructions && (
            <div className="flex min-w-0 items-start gap-2.5 rounded-xl border border-amber-200/80 bg-amber-50/70 p-2.5 sm:p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50 mt-0.5">
                <Info className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300" />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-bold text-amber-900 dark:text-amber-100">Payment instructions</h4>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-xs leading-relaxed text-amber-800/90 dark:text-amber-200">{selectedPaymentMethod.instructions}</p>
              </div>
            </div>
          )}

          {selectedPaymentMethod.details && (
            <div className="w-full min-w-0 max-w-full space-y-2.5 overflow-hidden rounded-2xl border border-gray-200/80 bg-white p-3 sm:p-3.5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex min-w-0 items-center justify-between gap-2">
                <label className="text-xs font-bold text-gray-800 dark:text-gray-200">
                  {selectedPaymentMethod.category === 'Crypto' ? 'Wallet address' : 'Receiver details'}
                </label>
                {selectedPaymentMethod.category === 'Crypto' && selectedPaymentMethod.network && (
                  <span className="rounded bg-blue-50 px-1.5 py-0.2 text-[9px] font-bold uppercase text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {selectedPaymentMethod.network}
                  </span>
                )}
              </div>
              <div className="relative min-w-0">
                <input
                  type="text"
                  readOnly
                  value={selectedPaymentMethod.details}
                  className="w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50/90 py-2.5 pl-3 pr-20 font-mono text-xs text-gray-700 outline-none dark:border-gray-700 dark:bg-gray-950/40 dark:text-gray-200 sm:pr-24"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(selectedPaymentMethod.details.toString())}
                  disabled={copyLoading}
                  className="absolute bottom-1 right-1 top-1 inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-bold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {copyLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
                  {copyLoading ? 'Copying' : 'Copy'}
                </button>
              </div>

              {selectedPaymentMethod.qrCode && (
                <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50/90 dark:bg-gray-950/40 p-2.5">
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => setExpandedQrOpen(true)}
                      className="group shrink-0 rounded-lg border border-gray-200 bg-white p-1.5 shadow-sm transition hover:border-primary-300 dark:border-gray-700 dark:bg-gray-900"
                      aria-label="Enlarge payment QR code"
                    >
                      <img src={selectedPaymentMethod.qrCode} alt="Payment QR" className="h-14 w-14 sm:h-16 sm:w-16 object-contain" />
                    </button>
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase text-gray-400">Scan to pay</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Tap to enlarge QR</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setExpandedQrOpen(true)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                      aria-label="Enlarge QR code"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={downloadQrCode}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white transition hover:bg-primary-700"
                      aria-label="Download QR code"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-200/80 bg-white p-3 sm:p-3.5 shadow-sm dark:border-gray-800 dark:bg-gray-900 space-y-2">
            <label className="block text-xs font-bold text-gray-800 dark:text-gray-200">{proofLabel}</label>
            {selectedPaymentMethod.category === 'Gift Card' ? (
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><Gift className="h-4 w-4 text-gray-400" /></div>
                <input ref={giftCardInputRef} type="text" name="giftCardNumber" value={formData.giftCardNumber} onChange={handleInputChange} className="w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50/90 py-2.5 pl-9 pr-3 font-mono text-xs text-gray-900 outline-none transition focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-950/40 dark:text-white" placeholder={proofPlaceholder} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
              </div>
            ) : (
              <input ref={transactionIdInputRef} type="text" name="transactionId" value={formData.transactionId} onChange={handleInputChange} className="w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50/90 px-3 py-2.5 text-xs text-gray-900 outline-none transition focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-950/40 dark:text-white" placeholder={proofPlaceholder} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
            )}
            <p className="text-[10px] leading-relaxed text-gray-400 dark:text-gray-500">Orders are manually verified. Submit the exact reference from your payment receipt.</p>
          </div>
        </div>
      )}
    </div>
  );

    const renderOrderSummary = ({ compact = false } = {}) => (
    <div className={`${compact ? '' : 'sticky top-0'} w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900`}>
      <div className="flex items-center justify-between gap-2.5 border-b border-gray-100 px-3.5 py-2.5 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            <ReceiptText className="h-3.5 w-3.5" />
          </span>
          <div>
            <h3 className="text-xs font-black text-gray-950 dark:text-white leading-tight">Order summary</h3>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{cartItems.length} item{cartItems.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-black text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
          {paymentSummary.currency}
        </span>
      </div>

      <div className={`${compact ? 'max-h-28 sm:max-h-36' : 'max-h-52'} overflow-y-auto overflow-x-hidden px-3 py-2 sm:px-3.5`}>
        <div className="space-y-2">
          {cartItems.map((item) => (
            <div key={`${item.id}-${item.validity || 'default'}`} className="flex items-center gap-2.5">
              <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                {item.image ? (
                  <img src={item.image} alt={item.name || item.productName || 'Product'} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-gray-400">
                    <ShoppingBag className="h-4 w-4" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-gray-900 dark:text-white leading-tight">{item.name || item.productName || item.id}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  {item.upgradeToValidity
                    ? `Upgrade (${formatDurationLabel(item.upgradeToValidity)})`
                    : item.extensionFor
                      ? `Ext (${item.validity ? formatDurationLabel(item.validity) : ''})`
                      : formatDurationLabel(item.validity || 'Lifetime')} {item.quantity > 1 ? `×${item.quantity}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right text-xs font-black text-gray-900 dark:text-white notranslate" translate="no">
                {formatPrice(resolvePriceValue(item.price) * (item.quantity || 1))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-1.5 border-t border-gray-100 px-3.5 py-2.5 text-xs dark:border-gray-800">
        <div className="flex justify-between text-gray-500 dark:text-gray-400">
          <span>Subtotal</span>
          <span className="font-semibold text-gray-900 dark:text-white notranslate" translate="no">{formatPrice(subtotal)}</span>
        </div>
        {commissionFee > 0 && (
          <div className="flex justify-between text-blue-600 dark:text-blue-400">
            <span>Fee ({settings?.commission || 15}%)</span>
            <span className="font-bold notranslate" translate="no">+{formatPrice(commissionFee)}</span>
          </div>
        )}
        {pointsDiscount > 0 && (
          <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
            <span>Wallet discount</span>
            <span className="font-bold notranslate" translate="no">-{formatPrice(pointsDiscount)}</span>
          </div>
        )}
        {promoDiscount > 0 && (
          <div className="flex justify-between text-primary-600 dark:text-primary-400">
            <span>Coupon discount</span>
            <span className="font-bold notranslate" translate="no">-{formatPrice(promoDiscount)}</span>
          </div>
        )}
        {shipping > 0 && (
          <div className="flex justify-between text-gray-500 dark:text-gray-400">
            <span>Shipping</span>
            <span className="font-semibold text-gray-900 dark:text-white notranslate" translate="no">{formatPrice(shipping)}</span>
          </div>
        )}
        <div className="mt-2 flex min-w-0 items-baseline justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
          <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Payable</span>
          <span className="shrink-0 text-lg sm:text-xl font-black text-primary-600 dark:text-primary-300 notranslate" translate="no">{formatPrice(finalTotal)}</span>
        </div>
      </div>
    </div>
  );

    const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="w-full min-w-0 max-w-full space-y-3 sm:space-y-4">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-black text-gray-950 dark:text-white">
                {isZeroTotal ? 'Order Covered 100%' : 'Payment method'}
              </h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {isZeroTotal
                  ? 'Your order is completely covered by discounts or reward points. No payment is required.'
                  : 'Pick one available method. Fees and payment instructions update automatically.'}
              </p>
            </div>

            {/* Mobile collapsible order summary on Step 1 */}
            {cartItems.length > 0 && !isZeroTotal && (
              <div className="lg:hidden">
                <button
                  type="button"
                  onClick={() => setMobileSummaryExpanded((prev) => !prev)}
                  className="flex w-full items-center justify-between rounded-xl border border-gray-200/90 bg-white px-3 py-2 text-xs font-bold text-gray-800 shadow-xs dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 transition-colors hover:border-gray-300 dark:hover:border-gray-700"
                >
                  <div className="flex items-center gap-2">
                    <ReceiptText className="h-3.5 w-3.5 text-primary-500" />
                    <span>Order summary ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-black text-primary-600 dark:text-primary-300 notranslate" translate="no">{formatPrice(finalTotal)}</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${mobileSummaryExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                <AnimatePresence>
                  {mobileSummaryExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-2 overflow-hidden"
                    >
                      {renderOrderSummary({ compact: true })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            {isZeroTotal ? (
              <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50/40 to-emerald-50 p-5 text-center dark:border-emerald-800/60 dark:bg-emerald-950/20 sm:p-6">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 shadow-sm dark:bg-emerald-900/50 dark:text-emerald-400">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h4 className="text-base font-black text-emerald-950 dark:text-emerald-100">
                  100% Free Order
                </h4>
                <p className="mt-1.5 text-xs text-emerald-800/80 dark:text-emerald-300/80 max-w-md mx-auto">
                  {appliedCoupon?.code && appliedPoints > 0
                    ? `Coupon "${appliedCoupon.code}" and reward points cover the entire total.`
                    : appliedCoupon?.code
                      ? `Coupon "${appliedCoupon.code}" gives you a 100% discount on this order.`
                      : 'Your reward points cover the entire order amount.'}
                </p>
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-white/90 px-3 py-1.5 text-xs font-bold text-emerald-800 shadow-sm dark:bg-gray-800 dark:text-emerald-300">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  Instant Auto-Approval • No Transaction ID Needed
                </div>
              </div>
            ) : paymentLoading ? (
              <div className="flex w-full min-w-0 items-center justify-center rounded-2xl border border-gray-200 bg-white py-10 dark:border-gray-800 dark:bg-gray-900">
                <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
                <span className="ml-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Loading payment methods...</span>
              </div>
            ) : (
              <div className="grid w-full min-w-0 max-w-full grid-cols-1 gap-2 sm:grid-cols-2">
                {activePaymentMethods.length === 0 ? (
                  <div className="sm:col-span-2 rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">No payment methods available</div>
                ) : (
                  activePaymentMethods.map((method) => (
                    <button
                      type="button"
                      key={method.id}
                      onClick={() => handlePaymentMethodSelect(method)}
                      className={`group min-h-[64px] sm:min-h-[70px] w-full min-w-0 max-w-full overflow-hidden rounded-xl border p-2.5 sm:p-3 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-950 ${
                        selectedPaymentMethod?.id === method.id
                          ? 'border-primary-500 bg-primary-50/80 shadow-md shadow-primary-500/10 dark:border-primary-500 dark:bg-primary-900/25'
                          : 'border-gray-200/90 bg-white hover:border-primary-300 hover:shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-700'
                      }`}
                    >
                      <div className="flex h-full min-w-0 items-center justify-between gap-2.5">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <PaymentMethodIcon method={method} size="md" showNetworkBadge={true} />
                          <div className="min-w-0">
                            <span className="block truncate text-xs sm:text-sm font-bold text-gray-950 dark:text-white leading-snug">{method.name}</span>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                              <span className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400">{method.category}</span>
                              {method.network && (
                                <span className="inline-flex rounded px-1.5 py-0.2 text-[9px] font-bold uppercase bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                  {method.network}
                                </span>
                              )}
                              {isLitecoinMethod(method) && (
                                exchangeRates.loading && selectedPaymentMethod?.id === method.id ? (
                                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.2 text-[9px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                    <Loader2 className="h-2.5 w-2.5 animate-spin" /> Live rate...
                                  </span>
                                ) : exchangeRates.ltc && exchangeRates.ltcUsdtPrice ? (
                                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.2 text-[9px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                    ● 1 LTC = ${exchangeRates.ltcUsdtPrice.toFixed(2)}
                                  </span>
                                ) : selectedPaymentMethod?.id === method.id && exchangeRates.error ? (
                                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.2 text-[9px] font-bold bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                                    ⚠ Rate pending
                                  </span>
                                ) : null
                              )}
                            </div>
                          </div>
                        </div>
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                          selectedPaymentMethod?.id === method.id
                            ? 'border-primary-500 bg-primary-500 text-white'
                            : 'border-gray-300 text-transparent group-hover:border-primary-300 dark:border-gray-700'
                        }`}>
                          <Check className="h-3 w-3" />
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
            <div className="flex w-full min-w-0 items-center gap-2.5 rounded-xl border border-emerald-200/80 bg-emerald-50/70 p-2.5 sm:p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div className="min-w-0">
                <p className="text-xs font-bold text-emerald-800 dark:text-emerald-200 leading-tight">Secure checkout</p>
                <p className="text-[11px] leading-snug text-emerald-700 dark:text-emerald-300">Cart validated and orders manually verified for buyer safety.</p>
              </div>
            </div>
          </div>
        );

            case 2:
        const paymentAmount = paymentSummary;
        const totalOriginalPrice = cartItems.reduce((acc, item) => {
          const price = typeof item.originalPrice === 'number' ? item.originalPrice : resolvePriceValue(item.price);
          return acc + price * (item.quantity || 1);
        }, 0);
        const totalCurrentPrice = cartItems.reduce((acc, item) => acc + resolvePriceValue(item.price) * (item.quantity || 1), 0);
        const storeSavings = Math.max(0, totalOriginalPrice - totalCurrentPrice);

        return renderPaymentDetailsContent(paymentAmount, storeSavings);
      default: return null;
    }
  };

  useEffect(() => {
    if (isOpen && !isAuthenticated) {
      onClose();
      toast.error('Please sign in to continue with the checkout process');
      setTimeout(() => navigate(getAuthRedirectPath()), 1000);
    }
  }, [isOpen, isAuthenticated, onClose, navigate]);

  useEffect(() => {
    if (!isOpen) setShowSuccessModal(false);
  }, [isOpen]);

  const renderInPortal = (content) => {
    if (typeof document === 'undefined') return content;
    return createPortal(content, document.body);
  };

  if (!isOpen) return null;
  if (!isAuthenticated) {
    return renderInPortal(
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110]" onClick={onClose} />
            <div className="fixed inset-0 z-[111] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 30 }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 lg:p-8 max-w-md w-full mx-4" >
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4"><User className="w-8 h-8 text-primary-600 dark:text-primary-400" /></div>
                  <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2">Sign In Required</h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">Please sign in to your account to continue with the checkout process.</p>
                  <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200">Cancel</button>
                    <button onClick={() => { onClose(); navigate(getAuthRedirectPath()); }} className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-xl transition-colors duration-200">Sign In</button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    );
  }

  const handleSuccessDismiss = () => {
    setShowSuccessModal(false);
    onClose();
    if (!isDirectCheckout) dispatch(setCartOpen(false));
    navigate(isScriptRequestCheckout ? '/script-requests' : '/products', { replace: isScriptRequestCheckout });
  };
  const handleViewOrders = () => {
    setShowSuccessModal(false);
    onClose();
    if (!isDirectCheckout) dispatch(setCartOpen(false));
    navigate(isScriptRequestCheckout ? '/script-requests' : '/orders', { replace: true });
  };

  return renderInPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-gray-950/80 backdrop-blur-sm z-[110]" onClick={onClose} />
          <div className="checkout-viewport-layer">
            <div className="checkout-center-shell">
              <motion.div
                ref={modalShellRef}
                tabIndex={-1}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 28 }}
                className="flex h-full w-full min-w-0 flex-col overflow-hidden bg-gray-50 shadow-2xl outline-none dark:bg-gray-950 sm:rounded-3xl"
              >
                <div className="shrink-0 border-b border-gray-200/80 bg-white/95 px-3.5 py-3 backdrop-blur dark:border-gray-800 dark:bg-gray-900/95 sm:px-4 lg:px-6">
                  <div className="flex items-center justify-between gap-3 sm:gap-4">
                    <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
                      <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300 shadow-xs">
                        <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h2 className="truncate text-base sm:text-lg font-black text-gray-950 dark:text-white">Secure Checkout</h2>
                          {!isZeroTotal && (
                            <span className="sm:hidden inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                              {step}/2
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">{currentStepSubtitle}</p>
                      </div>
                    </div>

                    {!isZeroTotal && (
                      <div className="hidden sm:flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800/80 p-1 text-[11px] font-bold border border-gray-200/60 dark:border-white/5">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full transition-all ${
                          step === 1
                            ? 'bg-white dark:bg-gray-900 text-primary-600 dark:text-primary-400 shadow-xs'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-black ${
                            step === 1 ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}>1</span>
                          Method
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">›</span>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full transition-all ${
                          step === 2
                            ? 'bg-white dark:bg-gray-900 text-primary-600 dark:text-primary-400 shadow-xs'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-black ${
                            step === 2 ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}>2</span>
                          Payment
                        </span>
                      </div>
                    )}

                    <button type="button" onClick={onClose} className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white" aria-label="Close checkout">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div ref={contentScrollRef} className="checkout-scroll-area min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
                    <div className="grid w-full min-w-0 max-w-full gap-3.5 px-3 py-3.5 pb-28 sm:p-4 sm:pb-28 lg:mx-auto lg:max-w-6xl lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_360px] lg:gap-5 lg:p-5 lg:pb-6">
                      <main className="w-full min-w-0 max-w-full overflow-hidden">
                        {renderStepContent()}
                      </main>
                      <aside className="hidden lg:block">
                        {renderOrderSummary()}
                      </aside>
                      <div className={`${step === 1 ? 'hidden' : 'block'} w-full min-w-0 max-w-full overflow-hidden lg:hidden`}>
                        {renderOrderSummary({ compact: true })}
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0 border-t border-gray-200/80 bg-white/95 px-3 py-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur dark:border-gray-800 dark:bg-gray-900/95 sm:px-4 sm:py-3 lg:p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
                    <div className="flex w-full min-w-0 max-w-full flex-col gap-2.5 sm:gap-3 lg:mx-auto lg:max-w-6xl lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex min-w-0 items-center justify-between gap-3 lg:block">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Payable total</p>
                        <p className="shrink-0 text-xl sm:text-2xl font-black text-primary-600 dark:text-primary-300 notranslate" translate="no">{formatPrice(finalTotal)}</p>
                      </div>
                      <ActionButtons step={step} handlePrevStep={handlePrevStep} handleNextStep={handleNextStep} loading={loading} finalTotal={finalTotal} paymentSummary={paymentSummary} selectedPaymentMethod={selectedPaymentMethod} isZeroTotal={isZeroTotal} onPlaceFreeOrder={placeFreeOrder} rateLoading={isLitecoinMethod(selectedPaymentMethod) && exchangeRates.loading && (!exchangeRates.ltc || exchangeRates.ltc <= 0)} />
                    </div>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
          <AnimatePresence>
            {expandedQrOpen && selectedPaymentMethod?.qrCode && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[125] bg-black/80 backdrop-blur-md"
                  onClick={() => setExpandedQrOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.94, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.94, y: 20 }}
                  className="fixed inset-0 z-[126] flex items-center justify-center p-4"
                  onClick={(event) => {
                    if (event.target === event.currentTarget) setExpandedQrOpen(false);
                  }}
                >
                  <div className="w-full max-w-xs rounded-3xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-gray-800 dark:bg-gray-900">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-base font-black text-gray-950 dark:text-white">Payment QR code</h3>
                        <p className="truncate text-xs font-semibold text-gray-500 dark:text-gray-400">{selectedPaymentMethod.name}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedQrOpen(false)}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                        aria-label="Close enlarged QR code"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="rounded-2xl bg-white p-3 ring-1 ring-gray-200 dark:ring-gray-700">
                      <img src={selectedPaymentMethod.qrCode} alt="Enlarged payment QR" className="mx-auto aspect-square w-full max-w-[260px] object-contain" />
                    </div>
                    <button
                      type="button"
                      onClick={downloadQrCode}
                      className="mt-3 flex min-h-10 w-full items-center justify-center gap-2 rounded-2xl bg-primary-600 px-4 text-xs font-black text-white transition hover:bg-primary-700"
                    >
                      <Download className="h-4 w-4" />
                      Download QR code
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showSuccessModal && (
              <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-md" />
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="fixed inset-0 z-[131] flex items-center justify-center p-6">
                  <div className="max-w-sm w-full bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 space-y-5 text-center border border-gray-200 dark:border-gray-700">
                    <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"><CheckCircle className="w-8 h-8 text-green-500" /></div>
                    <div className="space-y-2"><h3 className="text-2xl font-bold text-gray-900 dark:text-white">{isScriptRequestCheckout ? 'Request Submitted' : 'Order Successful'}</h3><p className="text-sm text-gray-600 dark:text-gray-400">{isScriptRequestCheckout ? 'Your script request payment proof was submitted. Please wait for admin verification.' : 'Thank you for your purchase. Please wait for order approval; status will be updated within 24 hours.'}</p></div>
                    <div className="flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0"><Button variant="outline" className="flex-1" onClick={handleSuccessDismiss}>{isScriptRequestCheckout ? 'Close' : 'Continue Shopping'}</Button><Button variant="primary" className="flex-1" onClick={handleViewOrders}>{isScriptRequestCheckout ? 'View Requests' : 'View Orders'}</Button></div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

const ActionButtons = ({ step, handlePrevStep, handleNextStep, loading, finalTotal, paymentSummary, selectedPaymentMethod, isZeroTotal, onPlaceFreeOrder, rateLoading = false }) => {
  if (isZeroTotal) {
    return (
      <div className="flex w-full min-w-0 gap-2.5 lg:w-auto lg:min-w-[300px]">
        <button
          type="button"
          onClick={onPlaceFreeOrder}
          disabled={loading}
          className="inline-flex min-h-11 sm:min-h-12 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl sm:rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 sm:px-6 py-2.5 text-sm font-black text-white shadow-md shadow-emerald-500/20 transition-all duration-200 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 lg:px-8"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
          {loading ? 'Processing...' : 'Place Free Order - $0.00'}
        </button>
      </div>
    );
  }

  const resolvedAmount = Number(paymentSummary.amount);
  const buttonLabel = !Number.isNaN(resolvedAmount) ? `${paymentSummary.amount} ${paymentSummary.currency}` : `${finalTotal.toFixed(2)} USDT`;
  return (
    <div className="flex w-full min-w-0 gap-2.5 lg:w-auto lg:min-w-[300px]">
      {step > 1 && (
        <button
          type="button"
          onClick={handlePrevStep}
          className="inline-flex min-h-11 sm:min-h-12 flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-xl sm:rounded-2xl border border-gray-300 bg-white px-4 text-xs sm:text-sm font-black text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}
      {step < 2 ? (
        <button
          type="button"
          className="inline-flex min-h-11 sm:min-h-12 min-w-0 flex-1 items-center justify-center rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-5 sm:px-6 py-2.5 text-xs sm:text-sm font-black text-white shadow-md shadow-primary-500/20 transition-all duration-200 hover:from-primary-600 hover:to-primary-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-950 lg:px-8"
          onClick={handleNextStep}
          disabled={!selectedPaymentMethod || rateLoading}
        >
          {rateLoading ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying Live Rate...
            </span>
          ) : (
            'Continue'
          )}
        </button>
      ) : (
        <Button type="submit" variant="primary" className="min-h-11 sm:min-h-12 min-w-0 flex-[1.6] rounded-xl sm:rounded-2xl text-xs sm:text-sm font-black lg:px-8" loading={loading} icon={CreditCard}>
          {loading ? 'Processing...' : `Place Order - ${buttonLabel}`}
        </Button>
      )}
    </div>
  );
};

export default CheckoutModal;

